import axios, { AxiosInstance } from 'axios'
import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import {
  BrandDashboard,
  BrandProfile,
  BrandProfileDetail,
  BrandProfileListItem,
  CompetitorInput,
  ContextTagType,
  CreateBrandProfilePayload,
  CreatePromptPayload,
  DeepDive,
  DetectContextResponse,
  ProfileState,
  PromptListItem,
  PromptListResponse,
  PromptStatus,
  PromptsOverview,
  SiteAuditResponse,
  UpdateBrandProfilePayload,
} from './brandProfile.types'

// Every endpoint of the redesign answers `{ success, data }`.
export interface AiListeningEnvelope<T> {
  data: T
  success: boolean
}

export interface AiListeningErrorEnvelope {
  code?: string
  error?: string
  success: false
}

export interface DateRangeParams {
  from?: string
  to?: string
}

export interface DashboardParams extends DateRangeParams {
  subject?: string
}

export interface PromptListParams {
  // Comma-separated context tag types; omitted = every prompt.
  context?: ContextTagType[]
  search?: string
  status?: PromptStatus | 'all'
}

export type CompetitorProfile = BrandProfile & { state: ProfileState }

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

const BASE = '/v1_private/ai-listening'

const profilePath = (profileId: string) =>
  `${BASE}/profiles/${encodeURIComponent(profileId)}`

// The brand profile redesign (spec: hapttic.knowledge_base
// features/ai-listening-redesign.md, section 5). Prompt-level endpoints that
// were kept as they were (reload, reload-status, smart insights) stay on
// AiListeningApiService.
class AiListeningBrandProfileApiService {
  private static _instance: AiListeningBrandProfileApiService

  private client: AxiosInstance

  private constructor() {
    this.client = axios.create({
      baseURL: ANALYTICS_API_URL,
      headers: getHeaders(),
    })
  }

  private static get instance(): AiListeningBrandProfileApiService {
    if (!AiListeningBrandProfileApiService._instance) {
      AiListeningBrandProfileApiService._instance =
        new AiListeningBrandProfileApiService()
    }
    return AiListeningBrandProfileApiService._instance
  }

  private static get<T>(url: string, params?: object): Promise<T> {
    return AiListeningBrandProfileApiService.instance.client
      .get<AiListeningEnvelope<T>>(url, { headers: getHeaders(), params })
      .then((response) => response.data.data)
  }

  private static post<T>(url: string, body: object): Promise<T> {
    return AiListeningBrandProfileApiService.instance.client
      .post<AiListeningEnvelope<T>>(url, body, { headers: getHeaders() })
      .then((response) => response.data.data)
  }

  private static put<T>(url: string, body: object): Promise<T> {
    return AiListeningBrandProfileApiService.instance.client
      .put<AiListeningEnvelope<T>>(url, body, { headers: getHeaders() })
      .then((response) => response.data.data)
  }

  private static delete(url: string): Promise<void> {
    return AiListeningBrandProfileApiService.instance.client
      .delete(url, { headers: getHeaders() })
      .then(() => undefined)
  }

  // ---------- profiles ----------

  public static getProfiles(): Promise<BrandProfileListItem[]> {
    return AiListeningBrandProfileApiService.get(`${BASE}/profiles`)
  }

  public static createProfile(
    payload: CreateBrandProfilePayload
  ): Promise<BrandProfile> {
    return AiListeningBrandProfileApiService.post(`${BASE}/profiles`, payload)
  }

  public static getProfile(profileId: string): Promise<BrandProfileDetail> {
    return AiListeningBrandProfileApiService.get(profilePath(profileId))
  }

  public static updateProfile(
    profileId: string,
    payload: UpdateBrandProfilePayload
  ): Promise<BrandProfile> {
    return AiListeningBrandProfileApiService.put(
      profilePath(profileId),
      payload
    )
  }

  public static deleteProfile(profileId: string): Promise<void> {
    return AiListeningBrandProfileApiService.delete(profilePath(profileId))
  }

  // ---------- competitors ----------

  public static getCompetitors(
    profileId: string
  ): Promise<CompetitorProfile[]> {
    return AiListeningBrandProfileApiService.get(
      `${profilePath(profileId)}/competitors`
    )
  }

  public static createCompetitor(
    profileId: string,
    payload: CompetitorInput
  ): Promise<BrandProfile> {
    return AiListeningBrandProfileApiService.post(
      `${profilePath(profileId)}/competitors`,
      payload
    )
  }

  public static updateCompetitor(
    profileId: string,
    competitorId: string,
    payload: Partial<CompetitorInput>
  ): Promise<BrandProfile> {
    return AiListeningBrandProfileApiService.put(
      `${profilePath(profileId)}/competitors/${encodeURIComponent(
        competitorId
      )}`,
      payload
    )
  }

  public static deleteCompetitor(
    profileId: string,
    competitorId: string
  ): Promise<void> {
    return AiListeningBrandProfileApiService.delete(
      `${profilePath(profileId)}/competitors/${encodeURIComponent(
        competitorId
      )}`
    )
  }

  // ---------- dashboard ----------

  public static getDashboard(
    profileId: string,
    params: DashboardParams
  ): Promise<BrandDashboard> {
    return AiListeningBrandProfileApiService.get(
      `${profilePath(profileId)}/dashboard`,
      params
    )
  }

  // ---------- site audit ----------

  public static getSiteAudit(
    profileId: string,
    subject: string
  ): Promise<SiteAuditResponse> {
    return AiListeningBrandProfileApiService.get(
      `${profilePath(profileId)}/site-audit`,
      { subject }
    )
  }

  public static runSiteAudit(
    profileId: string,
    subject: string
  ): Promise<SiteAuditResponse> {
    return AiListeningBrandProfileApiService.post(
      `${profilePath(profileId)}/site-audit?subject=${encodeURIComponent(
        subject
      )}`,
      {}
    )
  }

  // ---------- prompts ----------

  public static getPrompts(
    profileId: string,
    { context, search, status }: PromptListParams
  ): Promise<PromptListResponse> {
    return AiListeningBrandProfileApiService.get(
      `${profilePath(profileId)}/prompts`,
      {
        context: context?.length ? context.join(',') : undefined,
        search: search || undefined,
        status: status && status !== 'all' ? status : undefined,
      }
    )
  }

  public static createPrompt(
    profileId: string,
    payload: CreatePromptPayload
  ): Promise<PromptListItem> {
    return AiListeningBrandProfileApiService.post(
      `${profilePath(profileId)}/prompts`,
      payload
    )
  }

  public static getPromptsOverview(
    profileId: string,
    params: DateRangeParams
  ): Promise<PromptsOverview> {
    return AiListeningBrandProfileApiService.get(
      `${profilePath(profileId)}/prompts/overview`,
      params
    )
  }

  public static detectContext(prompt: string): Promise<DetectContextResponse> {
    return AiListeningBrandProfileApiService.post(
      `${BASE}/prompts/detect-context`,
      { prompt }
    )
  }

  // A prompt is still an ai_listening_campaign: status changes and deletes go
  // through the campaign endpoints, which merge a partial body server-side.
  public static updatePromptStatus(
    promptId: string,
    status: PromptStatus
  ): Promise<unknown> {
    return AiListeningBrandProfileApiService.put(
      `${BASE}/campaign/${encodeURIComponent(promptId)}`,
      { status }
    )
  }

  public static deletePrompt(promptId: string): Promise<void> {
    return AiListeningBrandProfileApiService.delete(
      `${BASE}/campaign/${encodeURIComponent(promptId)}`
    )
  }

  public static getDeepDive(
    promptId: string,
    checkedAt?: string,
    { from, to }: DateRangeParams = {}
  ): Promise<DeepDive> {
    return AiListeningBrandProfileApiService.get(
      `${BASE}/campaign/${encodeURIComponent(promptId)}/deep-dive`,
      { checkedAt, from, to }
    )
  }
}

export { AiListeningBrandProfileApiService }
